import React, { useState, useEffect, useCallback } from 'react';
import {
  Star, Building2, Calendar,
  Menu, X, LogOut, Loader2, ChevronRight,
  FileText,
  IndianRupee, MessageSquare, Tag,
  Bell, HelpCircle, Share2, CheckCircle2,
  Copy, Gift, Phone, Mail, BookOpen,
  ChevronDown, AlertCircle, Search, Shield, ScrollText,
  LayoutDashboard, Store, Gem, CalendarCheck, ChartNoAxesCombined, Sparkles,
  MessageSquareHeart, BadgePercent, BellRing, Settings2, Headset, ShieldCheck, Scale
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/axios';
import { DashboardShellSkeleton } from '../UI/Skeleton';
import ConfirmDialog from '../UI/ConfirmDialog';
import { useAuth } from '../../store/hooks/useRedux';

import DashboardOverview from './DashboardOverview';
import BusinessProfile from './BusinessProfile';
import ServicesManagement from './ServicesManagement';
import DocumentsSection from './DocumentsSection';
import PaymentSettings from './PaymentSettings';
import AccountSettings from './AccountSettings';
import VendorBookings from './VendorBookings';
import AnalyticsSection from '../../pages/Analytics/AnalyticsSection';
import AIEngine from '../../pages/AIEngine';
import logoSrc from '../../assets/mandapai-logo.png';

// ─── Nav Config ───────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    group: 'Main',
    items: [
      { id: 'overview',       icon: LayoutDashboard, label: 'Dashboard'         },
      { id: 'profile',        icon: Store,         label: 'Business Profile'  },
      { id: 'services',       icon: Gem,           label: 'Services'          },
      { id: 'bookings',       icon: CalendarCheck, label: 'Bookings'          },
      { id: 'analytics',      icon: ChartNoAxesCombined, label: 'Analytics'         },
      { id: 'ai-engine',      icon: Sparkles,      label: 'VendorLift'        },
    ],
  },
  {
    group: 'Operations',
    items: [
      { id: 'reviews',        icon: MessageSquareHeart, label: 'Reviews & Ratings' },
      { id: 'promotions',     icon: BadgePercent,  label: 'Promotions'        },
    ],
  },
  {
    group: 'Account',
    items: [
      { id: 'notifications',  icon: BellRing,      label: 'Notifications'     },
      { id: 'settings',       icon: Settings2,     label: 'Settings'          },
      { id: 'support',        icon: Headset,       label: 'Help & Support'    },
      { id: 'refer',          icon: Gift,          label: 'Refer & Earn'      },
    ],
  },
  {
    group: 'Legal',
    items: [
      { id: 'privacy',        icon: ShieldCheck,   label: 'Privacy Policy'    },
      { id: 'terms',          icon: Scale,         label: 'Terms & Conditions'},
    ],
  },
];

const ALL_NAV = NAV_GROUPS.flatMap(g => g.items);
const VALID_SECTIONS = ALL_NAV.map(n => n.id);
const DEFAULT_SECTION = 'overview';

const PAGE_DESC = {
  overview:      'Welcome to your business dashboard',
  profile:       'Manage your business information',
  services:      'Add and manage your services',
  bookings:      'View and manage customer bookings',
  analytics:     'Track your business performance',
  'ai-engine':   'AI-powered tools for your business',
  earnings:      'View your earnings and payout history',
  invoices:      'Manage and download your invoices',
  payments:      'Configure your payment methods',
  reviews:       'Read and respond to customer reviews',
  documents:     'Upload and verify your documents',
  promotions:    'Create offers and discount codes',
  notifications: 'Manage your notification preferences',
  settings:      'Configure your account settings',
  support:       'Get help and contact support',
  refer:         'Refer friends and earn rewards',
  privacy:       'How we collect and use your data',
  terms:         'Terms governing your use of the platform',
};

// ─── Hash helpers ─────────────────────────────────────────────────────────────
// Reads #section from the URL, falls back to DEFAULT_SECTION
const getSectionFromHash = () => {
  const hash = window.location.hash.replace('#', '');
  return VALID_SECTIONS.includes(hash) ? hash : DEFAULT_SECTION;
};

// ─── Placeholder Pages ────────────────────────────────────────────────────────

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="bg-white rounded-2xl border border-dashed border-gray-300 px-6 py-14 flex flex-col items-center justify-center text-center h-full">
    <div className="relative mb-5">
      <div className="absolute inset-0 rounded-2xl bg-purple-200/50 blur-xl" />
      <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 flex items-center justify-center">
        <Icon className="w-7 h-7 text-purple-500" />
      </div>
    </div>
    <h3 className="font-['Manrope'] text-base font-bold text-gray-900 mb-1.5">{title}</h3>
    <p className="text-sm text-gray-500 max-w-sm leading-relaxed">{description}</p>
    {action && (
      <button className="mt-6 inline-flex items-center justify-center h-10 px-5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold shadow-md shadow-purple-600/20 hover:shadow-lg transition-all">
        {action}
      </button>
    )}
  </div>
);

const EarningsSection = () => {
  const stats = [
    { label: 'Total Earnings', value: '₹0', sub: 'All time',    color: 'from-purple-500 to-pink-500' },
    { label: 'This Month',     value: '₹0', sub: 'April 2026',  color: 'from-purple-500 to-pink-400'   },
    { label: 'Pending Payout', value: '₹0', sub: 'Processing',  color: 'from-purple-400 to-pink-400'},
    { label: 'Completed Jobs', value: '0',  sub: 'Total jobs',   color: 'from-purple-400 to-pink-500'},
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-3`}>
              <IndianRupee className="w-5 h-5 text-white" />
            </div>
            <p className="font-['Manrope'] text-xl font-bold tracking-tight text-gray-900">{s.value}</p>
            <p className="text-sm text-gray-500 mt-0.5">{s.label}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>
      <EmptyState icon={IndianRupee} title="No Earnings Yet" description="Once you complete bookings, your earnings and payout history will appear here." />
    </div>
  );
};

const InvoicesSection = () => (
  <div className="space-y-4">
    <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-4 flex items-center gap-3">
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="Search invoices..." className="w-full pl-9 pr-4 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-200" />
      </div>
      <button className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-2">
        Filter <ChevronDown className="w-3.5 h-3.5" />
      </button>
    </div>
    <EmptyState icon={FileText} title="No Invoices Found" description="Invoices for your completed bookings will appear here." />
  </div>
);

// ─── Shared building blocks for the dashboard pages ───────────────────────────
const CARD = 'bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)]';
const PRIMARY_BTN = 'inline-flex items-center justify-center gap-2 h-10 px-5 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg transition-all';

// Soft gradient banner that opens each page
const PageBanner = ({ icon: Icon, title, description, children }) => (
  <div className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-gradient-to-br from-purple-50 via-white to-pink-50 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6 sm:p-7">
    <div className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-purple-200/40 blur-3xl" />
    <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
      <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20 flex items-center justify-center">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-['Manrope'] text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-1 leading-relaxed">{description}</p>
      </div>
      {children && <div className="flex-shrink-0">{children}</div>}
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className={`${CARD} p-5 flex items-center gap-4`}>
    <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-purple-50 flex items-center justify-center">
      <Icon className="w-5 h-5 text-purple-600" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      <p className="font-['Manrope'] text-2xl font-extrabold tracking-tight text-gray-900 tabular-nums leading-tight mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Toggle = ({ on, onChange, label, disabled = false, busy = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className={`relative w-11 h-6 flex-shrink-0 rounded-full transition-colors duration-200 ${on ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'bg-gray-200'} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center transition-transform duration-200 ${on ? 'translate-x-5' : 'translate-x-0'}`}>
      {busy && <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />}
    </span>
  </button>
);

const ReviewsSection = () => (
  <div className="space-y-5">
    <PageBanner
      icon={MessageSquare}
      title="Reviews & Ratings"
      description="See what customers say about you and how your rating builds up over time."
    />

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <StatCard icon={Star}          label="Average Rating" value="—" sub="Out of 5" />
      <StatCard icon={MessageSquare} label="Total Reviews"  value="0" sub="All time" />
      <StatCard icon={CheckCircle2}  label="Response Rate"  value="—" sub="Reviews you replied to" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-4">
      {/* Rating breakdown — empty bars keep the structure visible before the first review */}
      <div className={`${CARD} p-5 sm:p-6`}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Rating breakdown</p>
        <div className="mt-4 space-y-3">
          {[5, 4, 3, 2, 1].map(stars => (
            <div key={stars} className="flex items-center gap-3">
              <span className="flex w-8 items-center gap-1 text-xs font-semibold text-gray-600 tabular-nums">
                {stars} <Star className="w-3 h-3 text-gray-300" />
              </span>
              <div className="h-2 flex-1 rounded-full bg-gray-100" />
              <span className="w-6 text-right text-xs font-semibold text-gray-400 tabular-nums">0</span>
            </div>
          ))}
        </div>
        <p className="mt-5 text-xs text-gray-400 leading-relaxed">The breakdown fills in as customers rate their bookings.</p>
      </div>

      <EmptyState icon={MessageSquare} title="No Reviews Yet" description="Customer reviews and ratings will appear here after they complete a booking with you." />
    </div>
  </div>
);

const PromotionsSection = () => (
  <div className="space-y-5">
    <PageBanner
      icon={Tag}
      title="Promotions"
      description="Create discount codes and special offers to attract more customers to your services."
    >
      <button className={PRIMARY_BTN}>
        <Tag className="w-4 h-4" /> Create Promotion
      </button>
    </PageBanner>

    <EmptyState icon={Tag} title="No Active Promotions" description="Offers you create will be listed here with their status." />
  </div>
);

const NotificationsSection = () => {
  const groups = [
    {
      title: 'Bookings',
      items: [
        { key: 'newBooking', icon: Calendar,    label: 'New Booking',       desc: 'When a customer places a new booking', on: true },
        { key: 'cancelled',  icon: AlertCircle, label: 'Booking Cancelled', desc: 'When a customer cancels a booking',    on: true },
      ],
    },
    {
      title: 'Reviews & Payments',
      items: [
        { key: 'review',  icon: Star,        label: 'New Review',       desc: 'When a customer leaves a review',            on: true },
        { key: 'payment', icon: IndianRupee, label: 'Payment Received', desc: 'When a payout is processed to your account', on: true },
      ],
    },
    {
      title: 'From MandapAI',
      items: [
        { key: 'promo', icon: Bell, label: 'Promotional Updates', desc: 'Tips and product updates from us', on: false },
      ],
    },
  ];
  const allItems = groups.flatMap(g => g.items);
  const [toggles, setToggles] = useState(() => Object.fromEntries(allItems.map(p => [p.key, p.on])));
  const enabledCount = Object.values(toggles).filter(Boolean).length;
  const setAll = (value) => setToggles(Object.fromEntries(allItems.map(p => [p.key, value])));

  return (
    <div className="space-y-5">
      <PageBanner
        icon={Bell}
        title="Notifications"
        description={`${enabledCount} of ${allItems.length} alerts are switched on. Choose what you want to hear about.`}
      >
        <div className="flex gap-2">
          <button onClick={() => setAll(true)} className="h-10 px-4 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-purple-200 hover:bg-purple-50 transition-colors">
            Turn all on
          </button>
          <button onClick={() => setAll(false)} className="h-10 px-4 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
            Turn all off
          </button>
        </div>
      </PageBanner>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        {groups.map(group => (
          <div key={group.title} className={`${CARD} overflow-hidden`}>
            <p className="px-5 pt-5 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{group.title}</p>
            <div className="divide-y divide-gray-100">
              {group.items.map(pref => (
                <div key={pref.key} className="flex items-center gap-4 px-5 py-4">
                  <div className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors ${toggles[pref.key] ? 'bg-purple-50' : 'bg-gray-100'}`}>
                    <pref.icon className={`w-5 h-5 ${toggles[pref.key] ? 'text-purple-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">{pref.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{pref.desc}</p>
                  </div>
                  <Toggle
                    on={toggles[pref.key]}
                    label={pref.label}
                    onChange={() => setToggles(t => ({ ...t, [pref.key]: !t[pref.key] }))}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button onClick={() => toast.success('Notification preferences saved')} className={PRIMARY_BTN}>
          <CheckCircle2 className="w-4 h-4" /> Save Preferences
        </button>
      </div>
    </div>
  );
};

const SupportSection = () => {
  const faqs = [
    { q: 'How do I get my first booking?',         a: 'Complete your business profile and add your services. Customers can find and book you through the marketplace.' },
    { q: 'When do I receive my payout?',           a: 'Payouts are processed within 3–5 business days after a booking is marked complete.' },
    { q: 'How do I handle booking cancellations?', a: 'You can manage cancellations from the Bookings section. Your cancellation policy applies as per your profile settings.' },
    { q: 'How can I improve my search ranking?',   a: 'Keep your profile updated, respond quickly to bookings, and maintain a high rating to rank better.' },
  ];
  const [open, setOpen] = useState(0);
  const [query, setQuery] = useState('');
  const term = query.trim().toLowerCase();
  const visibleFaqs = term ? faqs.filter(f => `${f.q} ${f.a}`.toLowerCase().includes(term)) : faqs;

  return (
    <div className="space-y-5">
      {/* Hero with FAQ search */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-gradient-to-br from-purple-50 via-white to-pink-50 shadow-[0_1px_3px_rgba(16,24,40,0.04)] px-6 py-9 sm:py-11 text-center">
        <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="relative">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20 flex items-center justify-center">
            <HelpCircle className="w-6 h-6 text-white" />
          </div>
          <h3 className="font-['Manrope'] text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 mt-4">How can we help?</h3>
          <p className="text-sm text-gray-600 mt-1.5">Search the common questions or reach our team directly.</p>
          <div className="relative mx-auto mt-6 max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(0); }}
              placeholder="Search questions, e.g. payout"
              aria-label="Search frequently asked questions"
              className="w-full h-12 pl-11 pr-4 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Contact options */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Mail,     label: 'Email Support', value: 'support@yourapp.com', action: 'Send Email' },
          { icon: Phone,    label: 'Phone Support', value: '+91 1800-XXX-XXXX',   action: 'Call Now'   },
          { icon: BookOpen, label: 'Help Center',   value: 'Browse our guides',   action: 'Open Docs'  },
        ].map(c => (
          <div key={c.label} className={`${CARD} group p-5 hover:border-purple-200 hover:shadow-lg hover:shadow-purple-900/5 hover:-translate-y-0.5 transition-all duration-200`}>
            <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center">
              <c.icon className="w-5 h-5 text-purple-600" />
            </div>
            <p className="font-['Manrope'] text-base font-bold text-gray-900 mt-4">{c.label}</p>
            <p className="text-sm text-gray-500 mt-0.5">{c.value}</p>
            <button className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              {c.action}
              <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        ))}
      </div>

      {/* FAQ */}
      <div className={`${CARD} p-5 sm:p-6`}>
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="font-['Manrope'] text-base font-bold text-gray-900">Frequently Asked Questions</p>
          <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-500 tabular-nums">
            {visibleFaqs.length} of {faqs.length}
          </span>
        </div>
        {visibleFaqs.length ? (
          <div className="space-y-2">
            {visibleFaqs.map((faq, i) => {
              const isOpen = open === i;
              return (
                <div key={faq.q} className={`rounded-xl border transition-colors ${isOpen ? 'border-purple-200 bg-purple-50/40' : 'border-gray-200/70 bg-white hover:bg-gray-50'}`}>
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between gap-4 px-4 py-3.5 text-left"
                  >
                    <p className="text-sm font-semibold text-gray-900">{faq.q}</p>
                    <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${isOpen ? 'bg-white text-purple-600' : 'bg-gray-100 text-gray-500'}`}>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </span>
                  </button>
                  {isOpen && <p className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">{faq.a}</p>}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-200 px-4 py-10 text-center">
            <p className="text-sm font-semibold text-gray-900">No questions match "{query}"</p>
            <p className="text-sm text-gray-500 mt-1">Try a different word, or contact our team using the options above.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ReferSection = ({ vendorData }) => {
  const code = vendorData?.vendor?.referralCode || 'VENDOR123';
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const steps = [
    { icon: Share2,       title: 'Share your code', desc: 'Share your unique referral code with other vendors' },
    { icon: CheckCircle2, title: 'They sign up',    desc: 'They register using your referral code' },
    { icon: IndianRupee,  title: 'You earn ₹500',   desc: 'Reward is credited after their first booking is completed' },
  ];

  return (
    <div className="space-y-5">
      {/* Hero — offer on the left, code on the right */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 to-pink-500 p-6 sm:p-9 text-white shadow-lg shadow-purple-600/20">
        <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-28 -left-10 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
        <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
              <Gift className="w-3.5 h-3.5" /> Referral programme
            </span>
            <h3 className="font-['Manrope'] text-2xl sm:text-4xl font-extrabold tracking-tight mt-4">Refer & Earn ₹500</h3>
            <p className="text-sm sm:text-base text-white/85 mt-2 max-w-lg leading-relaxed">
              Invite fellow vendors to join the platform. Earn ₹500 for every vendor who completes their first booking.
            </p>
          </div>
          <div className="rounded-2xl bg-white p-5 text-gray-900 shadow-xl">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Your referral code</p>
            <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-dashed border-purple-300 bg-purple-50/60 px-4 py-3">
              <p className="font-mono text-lg font-bold tracking-[0.2em] text-gray-900 truncate">{code}</p>
              <Tag className="w-4 h-4 flex-shrink-0 text-purple-400" />
            </div>
            <button onClick={copy} className={`${PRIMARY_BTN} w-full mt-3`}>
              {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy code'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Share2}       label="Total Referrals" value="0" />
        <StatCard icon={CheckCircle2} label="Successful"      value="0" />
        <StatCard icon={IndianRupee}  label="Rewards Earned"  value="₹0" />
      </div>

      <div className={`${CARD} p-5 sm:p-7`}>
        <p className="font-['Manrope'] text-base font-bold text-gray-900 mb-6">How it works</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.title} className="relative">
              {/* Connector to the next step */}
              {i < steps.length - 1 && (
                <span className="hidden sm:block absolute top-6 left-16 right-0 h-px -mr-6 bg-gradient-to-r from-purple-200 to-transparent" />
              )}
              <div className="relative w-12 h-12 rounded-2xl bg-purple-50 border border-purple-100 flex items-center justify-center">
                <s.icon className="w-5 h-5 text-purple-600" />
                <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-[10px] font-bold text-white">
                  {i + 1}
                </span>
              </div>
              <p className="text-sm font-bold text-gray-900 mt-4">{s.title}</p>
              <p className="text-sm text-gray-500 mt-1 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Shared layout for the in-dashboard policy pages (Privacy, Terms)
const PolicyDoc = ({ icon: Icon, title, updated, intro, sections }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const jumpTo = (i) => {
    setActiveIdx(i);
    document.getElementById(`policy-section-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-5 w-full">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-gradient-to-br from-purple-50 via-white to-pink-50 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-6 sm:p-8">
        <div className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-purple-200/40 blur-3xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-start gap-5">
          <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20 flex items-center justify-center">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">Legal</p>
            <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 mt-1">{title}</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-2 leading-relaxed">{intro}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600">
                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                Last updated {updated}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600">
                <FileText className="w-3.5 h-3.5 text-gray-400" />
                {sections.length} sections
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick jump — chips on small screens */}
      <div className="flex gap-2 overflow-x-auto pb-1 lg:hidden">
        {sections.map((sec, i) => (
          <button
            key={i}
            onClick={() => jumpTo(i)}
            className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${
              activeIdx === i
                ? 'border-transparent bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20'
                : 'border-gray-200 bg-white text-gray-600 hover:border-purple-200 hover:text-gray-900'
            }`}
          >
            {sec.title}
          </button>
        ))}
      </div>

      <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-6 lg:items-start">

      {/* Section list — sticky side column on large screens */}
      <nav className="hidden lg:block sticky top-24 bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 px-2 mb-2">On this page</p>
        <div className="space-y-1">
          {sections.map((sec, i) => (
            <button
              key={i}
              onClick={() => jumpTo(i)}
              className={`w-full flex items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm transition-colors ${
                activeIdx === i ? 'bg-purple-50 font-semibold text-gray-900' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <span className="text-xs font-bold tabular-nums text-gray-400">{String(i + 1).padStart(2, '0')}</span>
              <span className="min-w-0 truncate">{sec.title}</span>
            </button>
          ))}
        </div>
      </nav>

      <div className="space-y-5 min-w-0">
      {/* Sections */}
      <div className="space-y-4">
        {sections.map((sec, i) => (
          <section
            key={i}
            id={`policy-section-${i}`}
            className="scroll-mt-24 bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-7"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-50 border border-purple-100 font-['Manrope'] text-xs font-bold text-gray-700 tabular-nums">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="font-['Manrope'] text-base sm:text-lg font-bold text-gray-900">{sec.title}</h3>
            </div>
            <div className="space-y-3 sm:pl-11">
              {sec.content.split('\n\n').map((para, j) => (
                <p key={j} className="text-sm sm:text-[15px] text-gray-600 leading-relaxed">{para}</p>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Contact */}
      <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-purple-50 flex items-center justify-center">
          <Mail className="w-5 h-5 text-purple-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-['Manrope'] text-sm font-bold text-gray-900">Questions about this page?</p>
          <p className="text-sm text-gray-500 mt-0.5">Write to our support team and we will help you out.</p>
        </div>
        <a
          href="mailto:support@mandapai.in"
          className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 hover:shadow-lg transition-all"
        >
          <Mail className="w-4 h-4" />
          support@mandapai.in
        </a>
      </div>
      </div>

      </div>
    </div>
  );
};

const PrivacyPolicy = () => {
  const sections = [
    { title: 'Information We Collect', content: `We collect information you provide directly to us when you create a vendor account, complete your business profile, or interact with our platform.\n\nWe also automatically collect certain information when you use our services, including log data, device information, and usage patterns.` },
    { title: 'How We Use Your Information', content: `We use the information we collect to operate and improve our platform, process bookings and payments, and communicate with you.\n\nWe do not sell your personal information to third parties.` },
    { title: 'Data Storage & Security', content: `Your data is stored on secure servers with industry-standard encryption.\n\nWe retain your data for as long as your account is active or as needed to provide services.` },
    { title: 'Your Rights', content: `You have the right to access, correct, or delete your personal information at any time.\n\nTo exercise any of these rights, please contact us at privacy@yourapp.com.` },
    { title: 'Changes to This Policy', content: `We may update this Privacy Policy from time to time. We will notify you of any significant changes by email or through a prominent notice on the platform.` },
  ];
  return (
    <PolicyDoc
      icon={Shield}
      title="Privacy Policy"
      updated="April 5, 2026"
      intro="This policy explains how we collect, use, and protect your personal information."
      sections={sections}
    />
  );
};

const TermsAndConditions = () => {
  const sections = [
    { title: 'Acceptance of Terms', content: `By registering as a vendor on our platform, you agree to be bound by these Terms and Conditions.\n\nWe reserve the right to update these terms at any time with reasonable notice.` },
    { title: 'Vendor Eligibility', content: `To register as a vendor, you must be at least 18 years of age and be a legitimate business entity or sole proprietor.\n\nWe reserve the right to verify your credentials and reject any vendor account that does not meet our criteria.` },
    { title: 'Bookings & Cancellations', content: `When a customer places a booking, you are obligated to fulfill that booking as described.\n\nRepeated cancellations or no-shows may result in penalties including account suspension.` },
    { title: 'Payments & Fees', content: `We charge a platform commission on each completed booking.\n\nPayouts are processed within 3–5 business days after booking completion.` },
    { title: 'Governing Law', content: `These terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of the courts in Hyderabad, Telangana, India.` },
  ];
  return (
    <PolicyDoc
      icon={ScrollText}
      title="Terms & Conditions"
      updated="April 5, 2026"
      intro="By using the platform, you agree to these terms."
      sections={sections}
    />
  );
};

// ─── Nav Item ─────────────────────────────────────────────────────────────────
const NavItem = ({ id, icon: Icon, label, active, expanded, onClick }) => (
  <button
    onClick={onClick}
    title={!expanded ? label : undefined}
    aria-current={active ? 'page' : undefined}
    className={`
      relative w-full flex items-center rounded-xl transition-all duration-200 group/nav
      ${expanded ? 'gap-3 px-2 py-1.5' : 'justify-center p-1.5'}
      ${active
        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-600/25'
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
    `}
  >
    {/* Icon tile */}
    <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-200 ${
      active
        ? 'bg-white/20 ring-1 ring-inset ring-white/25'
        : 'bg-gray-100/80 ring-1 ring-inset ring-gray-200/60 group-hover/nav:bg-purple-50 group-hover/nav:ring-purple-200/70'
    }`}>
      <Icon
        strokeWidth={1.75}
        className={`w-[17px] h-[17px] transition-colors ${active ? 'text-white' : 'text-gray-500 group-hover/nav:text-purple-600'}`}
      />
    </span>
    {expanded && <span className={`text-sm whitespace-nowrap ${active ? 'font-semibold' : 'font-medium'}`}>{label}</span>}
    {!expanded && (
      <span className="absolute left-full ml-3 px-3 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg whitespace-nowrap z-[999] shadow-lg opacity-0 -translate-x-2 pointer-events-none group-hover/nav:opacity-100 group-hover/nav:translate-x-0 transition-all duration-200">
        {label}
        <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
      </span>
    )}
  </button>
);

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ activeSection, onNavClick, mobileOpen, setMobileOpen, expanded, setExpanded, vendorData, onLogout }) => {
  const Inner = ({ forMobile = false }) => {
    const show = expanded || forMobile;
    return (
      <div className={`flex flex-col h-full select-none ${!forMobile && !expanded ? 'overflow-visible' : 'overflow-hidden'}`}>
        <div className={`flex items-center h-[4.5rem] px-4 border-b border-gray-100 transition-all duration-300 ${show ? 'justify-between' : 'justify-center'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <img src={logoSrc} alt="MandapAI" className="w-11 h-11 flex-shrink-0 select-none drop-shadow-sm" draggable={false} />
            {show && (
              <p className="min-w-0 truncate whitespace-nowrap font-['Manrope'] text-[15px] font-extrabold tracking-tight text-gray-900">Vendor Portal</p>
            )}
          </div>
          {!forMobile && show && (
            <button onClick={() => setExpanded(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <ChevronRight className="w-4 h-4 rotate-180 transition-transform duration-300" />
            </button>
          )}
          {forMobile && (
            <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {!forMobile && !expanded && (
          <button onClick={() => setExpanded(true)} className="mx-auto mt-3 w-6 h-6 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center hover:bg-gray-50 transition-colors">
            <ChevronRight className="w-3 h-3 text-gray-500" />
          </button>
        )}

        <nav className={`flex-1 px-3 py-3 space-y-0.5 ${(!forMobile && !expanded) ? 'overflow-visible' : 'overflow-y-auto'}`}>
          {NAV_GROUPS.map(({ group, items }, gi) => (
            <div key={group}>
              {gi > 0 && (
                show
                  ? <p className="mt-3 border-t border-gray-100 px-3 pt-4 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400">{group}</p>
                  : <div className="my-2 mx-2 border-t border-gray-100" />
              )}
              {items.map(({ id, icon, label }) => (
                <NavItem
                  key={id} id={id} icon={icon} label={label}
                  active={activeSection === id}
                  expanded={show}
                  onClick={() => {
                    onNavClick(id);
                    setMobileOpen(false);
                  }}
                />
              ))}
            </div>
          ))}
        </nav>

        <div className="border-t border-gray-100 p-3">
          <div className={`rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100/70 transition-all duration-200 ${show ? 'flex items-center gap-2.5 p-2.5' : 'flex flex-col items-center gap-2 p-2'}`}>
            {vendorData?.vendor && (
              <>
                <div className="w-9 h-9 flex-shrink-0 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-sm font-bold text-white shadow-md shadow-purple-600/20">
                  {vendorData.vendor.business_name?.charAt(0)?.toUpperCase() || <Building2 className="w-4 h-4" />}
                </div>
                {show && (
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-900 truncate">{vendorData.vendor.business_name}</p>
                    <p className="text-[10px] text-gray-500 truncate">{vendorData.vendor.contactDetails?.email}</p>
                  </div>
                )}
              </>
            )}
            <button
              onClick={onLogout}
              title="Logout"
              aria-label="Logout"
              className={`relative flex items-center justify-center rounded-lg text-gray-400 hover:bg-white hover:text-red-500 hover:shadow-sm transition-all group/out ${
                show && !vendorData?.vendor ? 'w-full gap-2 py-2 text-sm font-medium text-red-500' : 'h-8 w-8 flex-shrink-0'
              }`}
            >
              <LogOut className="w-[18px] h-[18px]" />
              {show && !vendorData?.vendor && <span>Logout</span>}
              {!show && (
                <span className="absolute left-full ml-3 px-3 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg whitespace-nowrap z-[999] shadow-lg opacity-0 -translate-x-2 pointer-events-none group-hover/out:opacity-100 group-hover/out:translate-x-0 transition-all duration-150">
                  Logout
                  <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-100 shadow-xl lg:hidden transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Inner forMobile />
      </aside>
      <aside className={`hidden lg:block fixed inset-y-3 left-3 z-40 bg-white rounded-2xl border border-gray-200/70 shadow-[0_8px_30px_rgba(88,28,135,0.08)] transition-all duration-300 ${expanded ? 'w-60 overflow-hidden' : 'w-[68px] overflow-visible'}`}>
        <Inner />
      </aside>
    </>
  );
};

// ─── Top Bar ──────────────────────────────────────────────────────────────────
const TopBar = ({ activeSection, setMobileOpen, vendorData }) => {
  const current = ALL_NAV.find(n => n.id === activeSection);
  const PageIcon = current?.icon;
  const isLive = !!vendorData?.vendor?.isLive;

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200/60 h-[4.5rem] px-5 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={() => setMobileOpen(true)} aria-label="Open menu" className="lg:hidden p-2 rounded-xl hover:bg-gray-100 text-gray-500">
          <Menu className="w-5 h-5" />
        </button>
        {PageIcon && (
          <div className="hidden sm:flex w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 items-center justify-center shadow-md shadow-purple-600/20">
            <PageIcon strokeWidth={1.75} className="w-5 h-5 text-white" />
          </div>
        )}
        <div className="min-w-0">
          <h2 className="font-['Manrope'] text-lg sm:text-xl font-extrabold tracking-tight text-gray-900 leading-tight truncate">
            {current?.label || activeSection}
          </h2>
          <p className="text-xs text-gray-500 hidden sm:block leading-tight mt-0.5 truncate">{PAGE_DESC[activeSection]}</p>
        </div>
      </div>

      {/* Live status — opens Settings, where visibility is changed */}
      <button
        type="button"
        onClick={() => { window.location.hash = 'settings'; }}
        title={isLive ? 'Your profile is visible to customers' : 'Your profile is hidden from customers'}
        className="group flex flex-shrink-0 items-center gap-2.5 rounded-full border border-gray-200/80 bg-white pl-2.5 pr-3.5 py-1.5 shadow-[0_1px_3px_rgba(16,24,40,0.06)] hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/5 transition-all"
      >
        <span className={`relative flex h-6 w-6 items-center justify-center rounded-full ${isLive ? 'bg-green-50' : 'bg-gray-100'}`}>
          {isLive && <span className="absolute h-2.5 w-2.5 rounded-full bg-green-400 opacity-60 animate-ping" />}
          <span className={`relative h-2.5 w-2.5 rounded-full ${isLive ? 'bg-green-500' : 'bg-gray-400'}`} />
        </span>
        <span className="text-left leading-tight">
          <span className="block text-xs font-bold text-gray-900">{isLive ? 'Online' : 'Offline'}</span>
          <span className="hidden sm:block text-[10px] font-medium text-gray-500">{isLive ? 'Visible to customers' : 'Hidden from customers'}</span>
        </span>
      </button>
    </header>
  );
};

// ─── Section Renderer ─────────────────────────────────────────────────────────
const SectionContent = ({ activeSection, vendorData, services, onUpdate, onNavigate }) => {
  switch (activeSection) {
    case 'overview':       return <DashboardOverview vendorData={vendorData} services={services} onNavigate={onNavigate} />;
    case 'profile':        return <BusinessProfile vendorData={vendorData} onUpdate={onUpdate} />;
    case 'services':       return <ServicesManagement onUpdate={onUpdate} />;
    case 'bookings':       return <VendorBookings />;
    case 'analytics':      return <AnalyticsSection vendorData={vendorData} services={services} />;
    case 'ai-engine':      return <AIEngine />;
    case 'earnings':       return <EarningsSection />;
    case 'invoices':       return <InvoicesSection />;
    case 'payments':       return <PaymentSettings vendorData={vendorData} onUpdate={onUpdate} />;
    case 'reviews':        return <ReviewsSection />;
    case 'documents':      return <DocumentsSection vendorData={vendorData} onUpdate={onUpdate} />;
    case 'promotions':     return <PromotionsSection />;
    case 'notifications':  return <NotificationsSection />;
    case 'settings':       return <AccountSettings vendorData={vendorData} onUpdate={onUpdate} onNavigate={onNavigate} />;
    case 'support':        return <SupportSection />;
    case 'refer':          return <ReferSection vendorData={vendorData} />;
    case 'privacy':        return <PrivacyPolicy />;
    case 'terms':          return <TermsAndConditions />;
    default:               return null;
  }
};

// ─── Dashboard Root ───────────────────────────────────────────────────────────
const VendorDashboard = () => {
  // ✅ Read initial section from URL hash — e.g. /dashboard#bookings
  const [activeSection, setActiveSection] = useState(getSectionFromHash);
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [expanded,      setExpanded]      = useState(true);
  const [loading,       setLoading]       = useState(false);
  const [vendorData,    setVendorData]    = useState(null);
  const [services,      setServices]      = useState([]);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const { logout } = useAuth();

  // ✅ When section changes, update the URL hash (pushState so back button works)
  const handleNavClick = useCallback((id) => {
    if (id === activeSection) return;
    window.location.hash = id;
    // State is updated by the hashchange listener below — no need to setActiveSection here
  }, [activeSection]);

  // ✅ Listen for browser back/forward — update section from hash
  useEffect(() => {
    const onHashChange = () => setActiveSection(getSectionFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // ✅ Set initial hash if missing
  useEffect(() => {
    if (!window.location.hash) {
      window.history.replaceState(null, '', `#${DEFAULT_SECTION}`);
    }
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [profileRes, servicesRes] = await Promise.all([
        api.get('/vendors/profile'),
        api.get('/api/v1/services'),
      ]);
      if (profileRes.data.success) setVendorData(profileRes.data.data);
      if (servicesRes.data.success)
        setServices(Object.values(servicesRes.data.data.services).flat());
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  // Ask before signing out — the popup below does the actual logout
  const handleLogout = () => setConfirmLogout(true);

  if (loading && !vendorData) {
    return <DashboardShellSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/60 via-gray-50 to-pink-50/60 font-['Inter']">
      <Sidebar
        activeSection={activeSection}
        onNavClick={handleNavClick}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        expanded={expanded}
        setExpanded={setExpanded}
        vendorData={vendorData}
        onLogout={handleLogout}
      />
      <div className={`flex flex-col min-h-screen transition-all duration-300 ${expanded ? 'lg:pl-[15.75rem]' : 'lg:pl-[calc(68px+0.75rem)]'}`}>
        <TopBar activeSection={activeSection} setMobileOpen={setMobileOpen} vendorData={vendorData} />
        <main className="flex-1 p-5 sm:p-6 lg:p-8">
          <SectionContent
            activeSection={activeSection}
            vendorData={vendorData}
            services={services}
            onUpdate={loadDashboardData}
            onNavigate={handleNavClick}
          />
        </main>
      </div>
      <ConfirmDialog
        open={confirmLogout}
        icon={LogOut}
        title="Are you sure you want to log out?"
        description="You will need to sign in again to manage your bookings and services."
        confirmLabel="Log out"
        cancelLabel="Stay signed in"
        onCancel={() => setConfirmLogout(false)}
        onConfirm={() => { setConfirmLogout(false); logout(); }}
      />
    </div>
  );
};

export default VendorDashboard;